import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, UserRole } from '../auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="login-container">
      <div class="login-card">

        <!-- Step 1: Email Input -->
        <div *ngIf="step === 'email'" class="step-content">
          <h1>Sign In</h1>
          <p class="subtitle">Enter your email to receive a verification code</p>

          <div class="form-group">
            <label for="email">Email</label>
            <input
              type="email"
              id="email"
              [(ngModel)]="email"
              placeholder="your&#64;email.com"
              [disabled]="isSubmitting"
              (keydown.enter)="sendCode()"
              class="form-control">
          </div>

          <button
            (click)="sendCode()"
            [disabled]="isSubmitting || !email"
            class="btn btn-primary btn-block">
            {{ isSubmitting ? 'Sending...' : 'Send Code' }}
          </button>

          <div *ngIf="message" class="alert" [ngClass]="{'alert-success': !error, 'alert-danger': error}">
            {{ message }}
          </div>
        </div>

        <!-- Step 2: Code Input -->
        <div *ngIf="step === 'code'" class="step-content">
          <h1>Verification Code</h1>
          <p class="subtitle">We sent a 6-digit code to<br><strong>{{ maskedEmail }}</strong></p>

          <div class="code-inputs" [class.shake]="shakeAnimation">
            <input
              #codeInput
              type="tel"
              inputmode="numeric"
              maxlength="1"
              autocomplete="off"
              (input)="onCodeInput(0, $event)"
              (keydown)="onCodeKeydown(0, $event)"
              (paste)="onCodePaste($event)"
              [disabled]="isSubmitting"
              class="code-input">
            <input
              #codeInput
              type="tel"
              inputmode="numeric"
              maxlength="1"
              autocomplete="off"
              (input)="onCodeInput(1, $event)"
              (keydown)="onCodeKeydown(1, $event)"
              (paste)="onCodePaste($event)"
              [disabled]="isSubmitting"
              class="code-input">
            <input
              #codeInput
              type="tel"
              inputmode="numeric"
              maxlength="1"
              autocomplete="off"
              (input)="onCodeInput(2, $event)"
              (keydown)="onCodeKeydown(2, $event)"
              (paste)="onCodePaste($event)"
              [disabled]="isSubmitting"
              class="code-input">
            <input
              #codeInput
              type="tel"
              inputmode="numeric"
              maxlength="1"
              autocomplete="off"
              (input)="onCodeInput(3, $event)"
              (keydown)="onCodeKeydown(3, $event)"
              (paste)="onCodePaste($event)"
              [disabled]="isSubmitting"
              class="code-input">
            <input
              #codeInput
              type="tel"
              inputmode="numeric"
              maxlength="1"
              autocomplete="off"
              (input)="onCodeInput(4, $event)"
              (keydown)="onCodeKeydown(4, $event)"
              (paste)="onCodePaste($event)"
              [disabled]="isSubmitting"
              class="code-input">
            <input
              #codeInput
              type="tel"
              inputmode="numeric"
              maxlength="1"
              autocomplete="off"
              (input)="onCodeInput(5, $event)"
              (keydown)="onCodeKeydown(5, $event)"
              (paste)="onCodePaste($event)"
              [disabled]="isSubmitting"
              class="code-input">
          </div>

          <button
            (click)="submitCode()"
            [disabled]="isSubmitting || !codeComplete"
            class="btn btn-primary btn-block verify-btn">
            {{ isSubmitting ? 'Verifying...' : 'Verify Code' }}
          </button>

          <div *ngIf="message" class="alert" [ngClass]="{'alert-success': !error, 'alert-danger': error}">
            {{ message }}
          </div>

          <div class="code-actions">
            <span *ngIf="resendCooldown > 0" class="resend-timer">
              Resend code in {{ resendCooldown }}s
            </span>
            <a *ngIf="resendCooldown === 0" (click)="resendCode()" class="resend-link">
              Resend code
            </a>
            <a (click)="useDifferentEmail()" class="back-link">
              Use different email
            </a>
          </div>
        </div>

        <!-- Step 3: Success -->
        <div *ngIf="step === 'success'" class="step-content success-step">
          <div class="success-icon">&#10003;</div>
          <h1>Verified!</h1>
          <p class="subtitle">Redirecting to dashboard...</p>
        </div>

      </div>
    </div>
  `,
  styles: [`
    .login-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: var(--ap-bg, #F6F7F9);
      padding: 24px 16px;
    }

    .login-card {
      background: var(--ap-surface, #fff);
      padding: 2rem;
      border-radius: var(--ap-radius-lg, 14px);
      border: 1px solid var(--ap-border, #E3E6EB);
      box-shadow: var(--ap-shadow-md);
      width: 100%;
      max-width: 400px;
    }

    .step-content { animation: fadeIn 0.25s ease-in-out; }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    h1 {
      margin-bottom: 0.4rem;
      color: var(--ap-text, #0B1220);
      font-size: 1.25rem;
      font-weight: 650;
      text-align: center;
      letter-spacing: -0.02em;
    }

    .subtitle {
      margin-bottom: 1.5rem;
      color: var(--ap-text-muted, #6B7280);
      text-align: center;
      font-size: 14px;
      line-height: 1.5;
    }

    .form-group { margin-bottom: 1.25rem; }

    label {
      display: block;
      margin-bottom: 5px;
      font-size: 12px;
      font-weight: 500;
      color: var(--ap-text-soft, #4B5563);
    }

    .form-control {
      width: 100%;
      padding: 9px 12px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 14px;
      font-family: var(--ap-font, 'Inter', sans-serif);
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
      box-sizing: border-box;
      transition: border-color 0.15s, box-shadow 0.15s;
    }

    .form-control:focus {
      outline: none;
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: var(--ap-ring, 0 0 0 3px rgba(47,91,218,.18));
    }

    .btn {
      padding: 10px 1rem;
      border: none;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 14px;
      font-weight: 550;
      font-family: var(--ap-font, 'Inter', sans-serif);
      transition: background-color 0.15s;
    }

    .btn-primary {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
    }

    .btn-primary:hover:not(:disabled) { background: var(--ap-primary-ink, #1E3FA8); }

    .btn-block { display: block; width: 100%; }

    .btn:disabled { opacity: 0.45; cursor: not-allowed; }

    .alert {
      margin-top: 1rem;
      padding: 10px 14px;
      border-radius: var(--ap-radius-sm, 6px);
      text-align: center;
      font-size: 13px;
      line-height: 1.45;
    }

    .alert-success { background: var(--ap-success-soft, #E3F4EC); color: var(--ap-success, #047A55); }
    .alert-danger  { background: var(--ap-danger-soft, #FDECEA);  color: var(--ap-danger, #B42318); }

    /* Code inputs */
    .code-inputs {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin: 1.5rem 0;
    }

    .code-input {
      width: 56px;
      height: 64px;
      text-align: center;
      font-size: 1.5rem;
      font-weight: 650;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius, 10px);
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
      font-family: var(--ap-mono, monospace);
      transition: border-color 0.15s, box-shadow 0.15s;
      -moz-appearance: textfield;
    }

    .code-input::-webkit-outer-spin-button,
    .code-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

    .code-input:focus {
      outline: none;
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: var(--ap-ring, 0 0 0 3px rgba(47,91,218,.18));
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
      20%, 40%, 60%, 80% { transform: translateX(6px); }
    }
    .shake { animation: shake 0.5s ease-in-out; }

    .code-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 1.25rem;
      font-size: 13px;
    }

    .resend-link { color: var(--ap-primary, #2F5BDA); cursor: pointer; text-decoration: none; }
    .resend-link:hover { text-decoration: underline; }
    .resend-timer { color: var(--ap-text-muted, #6B7280); }

    .back-link { color: var(--ap-text-muted, #6B7280); cursor: pointer; text-decoration: none; }
    .back-link:hover { color: var(--ap-text, #0B1220); text-decoration: underline; }

    .verify-btn { margin-top: 0.5rem; }

    /* Success step */
    .success-step { text-align: center; padding: 2rem 0; }

    .success-icon {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: var(--ap-success, #047A55);
      color: #fff;
      font-size: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;
      animation: scaleIn 0.3s ease-out;
    }

    @keyframes scaleIn {
      from { transform: scale(0); }
      to   { transform: scale(1); }
    }
  `]
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef>;

  public step: 'email' | 'code' | 'success' = 'email';
  public email = '';
  public maskedEmail = '';
  public codeComplete = false;
  public isSubmitting = false;
  public message = '';
  public error = false;
  public resendCooldown = 0;
  public shakeAnimation = false;
  public returnUrl = '/dashboard';

  private resendTimer: any = null;
  private userSub: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';

    if (true === this.authService.isLoggedIn) {
      this.router.navigate([this.returnUrl]);
    }

    this.userSub = this.authService.currentUser$.subscribe(user => {
      if (null !== user && this.step !== 'success') {
        this.router.navigate([this.returnUrl]);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
  }

  /** Read digit values directly from DOM inputs — single source of truth */
  private getCodeFromInputs(): string {
    const inputs = this.codeInputs?.toArray();
    if (!inputs) return '';
    return inputs.map(el => el.nativeElement.value).join('');
  }

  /** Update the codeComplete flag by reading actual DOM values */
  private updateCodeComplete(): void {
    const code = this.getCodeFromInputs();
    this.codeComplete = code.length === 6 && /^\d{6}$/.test(code);
  }

  private getInputEl(index: number): HTMLInputElement | null {
    const inputs = this.codeInputs?.toArray();
    return inputs && inputs[index] ? inputs[index].nativeElement : null;
  }

  public sendCode(): void {
    if (!this.email || this.isSubmitting) return;

    this.isSubmitting = true;
    this.message = '';
    this.error = false;

    this.authService.requestCode(this.email).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (true === response.success) {
          this.maskedEmail = this.maskEmail(this.email);
          this.step = 'code';
          this.codeComplete = false;
          this.message = '';
          this.startResendCooldown();
          setTimeout(() => {
            this.clearAllInputs();
            this.getInputEl(0)?.focus();
          }, 50);
        } else {
          this.message = response.message || 'Failed to send code.';
          this.error = true;
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.message = 'An error occurred. Please try again.';
        this.error = true;
      }
    });
  }

  public onCodeInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    // Strip non-digits and keep only first character
    const cleaned = input.value.replace(/\D/g, '');
    const digit = cleaned.charAt(0);

    // Set the cleaned value back on the DOM element
    input.value = digit;

    this.updateCodeComplete();

    // If a digit was entered, advance to next input
    if (digit && index < 5) {
      this.getInputEl(index + 1)?.focus();
    }
  }

  public onCodeKeydown(index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (input.value) {
        // Current box has a value — clear it, stay on this box
        input.value = '';
        this.updateCodeComplete();
        event.preventDefault();
      } else if (index > 0) {
        // Current box is empty — move to previous and clear it
        const prev = this.getInputEl(index - 1);
        if (prev) {
          prev.value = '';
          prev.focus();
          this.updateCodeComplete();
        }
        event.preventDefault();
      }
      return;
    }

    if (event.key === 'Delete') {
      input.value = '';
      this.updateCodeComplete();
      event.preventDefault();
      return;
    }

    // Arrow key navigation
    if (event.key === 'ArrowLeft' && index > 0) {
      this.getInputEl(index - 1)?.focus();
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowRight' && index < 5) {
      this.getInputEl(index + 1)?.focus();
      event.preventDefault();
      return;
    }

    // If a digit key is pressed and the box already has a value, replace it
    if (/^\d$/.test(event.key) && input.value) {
      input.value = event.key;
      this.updateCodeComplete();
      if (index < 5) {
        this.getInputEl(index + 1)?.focus();
      }
      event.preventDefault();
      return;
    }
  }

  public onCodePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text')?.trim() || '';
    const digits = pastedData.replace(/\D/g, '').substring(0, 6);

    if (digits.length === 0) return;

    const inputs = this.codeInputs?.toArray();
    if (!inputs) return;

    for (let i = 0; i < 6; i++) {
      inputs[i].nativeElement.value = digits[i] || '';
    }

    // Focus the next empty or the last filled input
    const focusIndex = Math.min(digits.length, 5);
    inputs[focusIndex].nativeElement.focus();

    this.updateCodeComplete();
  }

  public submitCode(): void {
    const code = this.getCodeFromInputs();
    if (code.length !== 6 || !/^\d{6}$/.test(code) || this.isSubmitting) return;

    this.isSubmitting = true;
    this.message = '';
    this.error = false;

    this.authService.validateCode(this.email, code).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (true === response.success) {
          this.step = 'success';
          this.message = '';
          setTimeout(() => {
            const user = this.authService.currentUserValue;
            if (user?.role === UserRole.PLATFORM_OWNER) {
              this.router.navigate(['/god/overview']);
            } else {
              this.router.navigate([this.returnUrl]);
            }
          }, 1000);
        } else {
          this.message = response.message || 'Invalid code.';
          this.error = true;
          this.triggerShake();
          this.clearAllInputs();
          setTimeout(() => this.getInputEl(0)?.focus(), 100);
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.message = 'An error occurred. Please try again.';
        this.error = true;
        this.triggerShake();
      }
    });
  }

  public resendCode(): void {
    if (this.resendCooldown > 0) return;
    this.message = '';
    this.error = false;
    this.isSubmitting = true;

    this.authService.requestCode(this.email).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (true === response.success) {
          this.message = 'A new code has been sent.';
          this.error = false;
          this.clearAllInputs();
          this.startResendCooldown();
          setTimeout(() => this.getInputEl(0)?.focus(), 100);
        } else {
          this.message = response.message || 'Failed to resend code.';
          this.error = true;
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.message = 'Failed to resend code. Please try again.';
        this.error = true;
      }
    });
  }

  public useDifferentEmail(): void {
    this.step = 'email';
    this.codeComplete = false;
    this.message = '';
    this.error = false;
    this.resendCooldown = 0;
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  private clearAllInputs(): void {
    const inputs = this.codeInputs?.toArray();
    if (inputs) {
      inputs.forEach(el => el.nativeElement.value = '');
    }
    this.codeComplete = false;
  }

  private maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const visible = local.substring(0, Math.min(3, local.length));
    const masked = '*'.repeat(Math.max(local.length - 3, 1));
    return `${visible}${masked}@${domain}`;
  }

  private startResendCooldown(): void {
    this.resendCooldown = 60;
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
    this.resendTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        this.resendCooldown = 0;
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    }, 1000);
  }

  private triggerShake(): void {
    this.shakeAnimation = true;
    setTimeout(() => this.shakeAnimation = false, 500);
  }
}
